import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { cn } from '../../lib/utils';
import { Clock, MapPin, Users, Zap } from 'lucide-react';

const formatTime12h = (time24) => {
  if (!time24) return '';
  const [h, m] = time24.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hr = h % 12 || 12;
  return `${hr}:${m.toString().padStart(2, '0')} ${ampm}`;
};

const getCountdown = (gameDate, gameTime) => {
  const now = new Date();
  let target;
  if (gameTime) {
    const [h, m] = gameTime.split(':').map(Number);
    target = new Date(gameDate);
    target.setHours(h, m, 0, 0);
  } else {
    target = new Date(gameDate);
    target.setHours(17, 0, 0, 0); // Default 5PM
  }
  const diff = target - now;
  if (diff <= 0) return null;

  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const minutes = Math.floor((diff % 3600000) / 60000);
  const seconds = Math.floor((diff % 60000) / 1000);
  return { days, hours, minutes, seconds, total: diff };
};

const TimeBlock = ({ value, label, pulse }) => (
  <div className="text-center">
    <div className={cn(
      "text-2xl md:text-3xl font-extrabold tabular-nums transition-all",
      pulse ? "text-amber-400 scale-105" : "text-white"
    )} style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
      {String(value).padStart(2, '0')}
    </div>
    <div className="text-[9px] text-stone-500 uppercase tracking-wider">{label}</div>
  </div>
);

export default function GameDayCountdown({ games = [], teamName = '' }) {
  const [countdown, setCountdown] = useState(null);
  const [nextGame, setNextGame] = useState(null);

  useEffect(() => {
    // Find the next upcoming game
    const now = new Date();
    const upcoming = games
      .filter(g => !g.is_complete)
      .map(g => {
        const d = new Date(g.date + 'T00:00:00');
        if (g.game_time) {
          const [h, m] = g.game_time.split(':').map(Number);
          d.setHours(h, m, 0, 0);
        } else {
          d.setHours(23, 59, 59, 0);
        }
        return { ...g, _targetDate: d };
      })
      .filter(g => g._targetDate > now)
      .sort((a, b) => a._targetDate - b._targetDate);

    if (upcoming.length > 0) {
      setNextGame(upcoming[0]);
    } else {
      setNextGame(null);
      setCountdown(null);
    }
  }, [games]);

  useEffect(() => {
    if (!nextGame) return;
    const tick = () => setCountdown(getCountdown(nextGame.date, nextGame.game_time));
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [nextGame]);

  if (!nextGame || !countdown) return null;

  const isToday = countdown.days === 0;
  const isSoon = countdown.total < 7200000; // < 2 hours

  return (
    <Card className={cn(
      "border-0 shadow-lg overflow-hidden",
      isSoon ? "bg-gradient-to-r from-amber-900 to-stone-900 border-l-4 border-l-amber-500" :
      isToday ? "bg-gradient-to-r from-green-900/60 to-stone-900 border-l-4 border-l-green-500" :
      "bg-gradient-to-r from-stone-900 to-stone-800 border-l-4 border-l-stone-600"
    )} data-testid="game-day-countdown">
      <CardContent className="py-4 px-5">
        <div className="flex items-start justify-between gap-4">
          {/* Game Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              {isSoon && <Zap size={14} className="text-amber-400 animate-pulse" />}
              <span className={cn(
                "text-xs font-bold uppercase tracking-wider",
                isSoon ? "text-amber-400" : isToday ? "text-green-400" : "text-stone-500"
              )}>
                {isSoon ? 'GAME TIME!' : isToday ? 'TODAY' : 'NEXT GAME'}
              </span>
            </div>
            <h3 className="text-white text-lg font-extrabold truncate" style={{ fontFamily: 'Barlow Condensed, sans-serif' }} data-testid="countdown-opponent">
              {teamName ? `${teamName} vs ` : 'vs '}{nextGame.opponent_name}
            </h3>
            <div className="flex flex-wrap items-center gap-3 mt-1.5 text-xs text-stone-400">
              <span className="flex items-center gap-1">
                <Clock size={10} />
                {nextGame.game_time ? formatTime12h(nextGame.game_time) : 'Time TBD'}
              </span>
              {nextGame.field_location && (
                <span className="flex items-center gap-1">
                  <MapPin size={10} />
                  {nextGame.field_location}
                </span>
              )}
              <span className="flex items-center gap-1">
                <Users size={10} />
                {nextGame.is_home ? 'Home' : 'Away'}
              </span>
            </div>
          </div>

          {/* Countdown Timer */}
          <div className="shrink-0 flex items-center gap-3" data-testid="countdown-timer">
            {countdown.days > 0 && (
              <>
                <TimeBlock value={countdown.days} label="Days" />
                <span className="text-stone-600 text-xl font-bold mt-[-12px]">:</span>
              </>
            )}
            <TimeBlock value={countdown.hours} label="Hrs" pulse={isSoon && countdown.hours === 0} />
            <span className="text-stone-600 text-xl font-bold mt-[-12px]">:</span>
            <TimeBlock value={countdown.minutes} label="Min" pulse={isSoon} />
            {countdown.days === 0 && (
              <>
                <span className="text-stone-600 text-xl font-bold mt-[-12px]">:</span>
                <TimeBlock value={countdown.seconds} label="Sec" pulse={isSoon} />
              </>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
